import type { DutyCapability, Persona, ResourceScope } from '@kop/contracts';
import { featureAccessProjectionStatus } from '../../feature-access-projection';

/**
 * Closed presentation vocabulary the server accepts on `board.display`. The list projection can
 * carry any of the six, so every one of them has to render; the compose screen offers only the
 * first three (see `titleColorChoices`).
 */
export const boardTitleColors = [
  'default',
  'critical',
  'warning',
  'info',
  'success',
  'muted',
] as const;
export type BoardTitleColor = (typeof boardTitleColors)[number];

export const boardEmphases = ['normal', 'important', 'urgent'] as const;
export type BoardEmphasis = (typeof boardEmphases)[number];
export const boardEmphasisLabel: Readonly<Record<BoardEmphasis, string>> = {
  normal: '일반',
  important: '중요',
  urgent: '긴급',
};

export const boardCategoryColors = ['navy', 'blue', 'green', 'amber', 'red', 'slate'] as const;
export type BoardCategoryColor = (typeof boardCategoryColors)[number];
export const boardCategoryIcons = ['megaphone', 'shield', 'alert', 'book', 'bell', 'info'] as const;
export type BoardCategoryIcon = (typeof boardCategoryIcons)[number];

export const boardCategories = [
  'urgent-notice',
  'general-notice',
  'incident-case',
  'legal-change',
  'external-siren',
] as const;
export type BoardCategory = (typeof boardCategories)[number];

export const boardCategoryLabel: Readonly<Record<BoardCategory, string>> = {
  'urgent-notice': '긴급 공지',
  'general-notice': '일반 공지',
  'incident-case': '사고 사례',
  'legal-change': '법령 개정',
  'external-siren': '외부 경보',
};

/** Audiences the compose screen offers. `contractors` is server-supported but not offered here. */
export const boardAudiences = ['corporate', 'site'] as const;
export type BoardAudience = (typeof boardAudiences)[number];

export const boardAudienceLabel: Readonly<Record<string, string>> = {
  corporate: '전사',
  site: '음성공장 전체',
  contractors: '지정 협력사',
};

export const boardStatusLabel: Readonly<Record<string, string>> = {
  open: '미발행',
  published: '발행됨',
  promoted: '조치 연계',
  archived: '보관됨',
};

/** The three the compose screen offers, mapped to tokens that already exist. */
export const titleColorChoices: readonly Readonly<{ value: BoardTitleColor; label: string }>[] = [
  { value: 'default', label: '기본' },
  { value: 'critical', label: '긴급' },
  { value: 'warning', label: '주의' },
];

/**
 * One row of `listSafetyBoardPosts`. The same projection backs the row and the detail dialog --
 * the server returns `body`, `audience`, `attachmentIds` and the display/read state on every
 * row -- so opening a post costs no further read.
 */
export type SafetyBoardPost = Readonly<{
  boardId: string;
  version: number;
  status: string;
  category: string;
  categoryLabel: string;
  title: string;
  summary: string;
  body: string;
  audience: string;
  publishedAt: string | null;
  updatedAt: string;
  attachmentIds: readonly string[];
  attachments: readonly BoardAttachment[];
  followUpActionId: string | null;
  pinned: boolean;
  titleColor: BoardTitleColor;
  emphasis: BoardEmphasis;
  /** Version of the sibling display document -- the `expectedVersion` a `display` command takes. */
  displayVersion: number;
  read: boolean;
  readAt: string | null;
}>;

export type SafetyBoardPage = Readonly<{
  items: readonly SafetyBoardPost[];
  complete: boolean;
  totalVisible: number;
  unreadVisible: number;
  settings?: SafetyBoardSettings | null;
}>;

export type SafetyBoardReadReceipt = Readonly<{
  boardId: string;
  read: boolean;
  readAt: string | null;
}>;

const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const text = (value: unknown): value is string => typeof value === 'string';
const nullableText = (value: unknown): value is string | null => value === null || text(value);
const tally = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;

const titleColor = (value: unknown): BoardTitleColor =>
  text(value) && (boardTitleColors as readonly string[]).includes(value)
    ? (value as BoardTitleColor)
    : 'default';
const emphasisOf = (value: BoardTitleColor): BoardEmphasis =>
  value === 'critical' ? 'urgent' : value === 'warning' ? 'important' : 'normal';

/**
 * Validates one projected row. A row missing a field the screen renders invalidates the whole
 * response rather than being patched up with a default, so nothing is drawn that the server did
 * not send. The single exception is `titleColor`: an unknown presentation token falls back to
 * `default`, which is what the server itself does when it reads the display document.
 */
export function decodeSafetyBoardPost(value: unknown): SafetyBoardPost | null {
  if (!record(value)) return null;
  if (!text(value.boardId) || !value.boardId) return null;
  if (!tally(value.version) || !tally(value.displayVersion)) return null;
  if (
    !text(value.status) ||
    !text(value.category) ||
    !text(value.categoryLabel) ||
    !text(value.title)
  )
    return null;
  if (!text(value.summary) || !text(value.body) || !text(value.audience)) return null;
  if (!nullableText(value.publishedAt) || !text(value.updatedAt)) return null;
  if (!nullableText(value.followUpActionId) || !nullableText(value.readAt)) return null;
  if (typeof value.pinned !== 'boolean' || typeof value.read !== 'boolean') return null;
  if (!Array.isArray(value.attachmentIds) || !value.attachmentIds.every(text)) return null;
  if (!Array.isArray(value.attachments)) return null;
  const attachments = value.attachments.flatMap((candidate) => {
    if (!record(candidate) || !text(candidate.manifestId) || !text(candidate.sha256)) return [];
    return [{ manifestId: candidate.manifestId, sha256: candidate.sha256 }];
  });
  if (attachments.length !== value.attachments.length) return null;
  const projectedTitleColor = titleColor(value.titleColor);
  return {
    boardId: value.boardId,
    version: value.version,
    status: value.status,
    category: value.category,
    categoryLabel: value.categoryLabel,
    title: value.title,
    summary: value.summary,
    body: value.body,
    audience: value.audience,
    publishedAt: value.publishedAt,
    updatedAt: value.updatedAt,
    attachmentIds: value.attachmentIds,
    attachments,
    followUpActionId: value.followUpActionId,
    pinned: value.pinned,
    titleColor: projectedTitleColor,
    emphasis: emphasisOf(projectedTitleColor),
    displayVersion: value.displayVersion,
    read: value.read,
    readAt: value.readAt,
  };
}

/** `null` means the page could not be read; it is never a stand-in for an empty board. */
export function decodeSafetyBoardPage(value: unknown): SafetyBoardPage | null {
  if (!record(value) || !Array.isArray(value.items)) return null;
  if (typeof value.complete !== 'boolean') return null;
  if (!tally(value.totalVisible) || !tally(value.unreadVisible)) return null;
  const items: SafetyBoardPost[] = [];
  for (const candidate of value.items) {
    const item = decodeSafetyBoardPost(candidate);
    if (!item) return null;
    items.push(item);
  }
  return {
    items,
    complete: value.complete,
    totalVisible: value.totalVisible,
    unreadVisible: value.unreadVisible,
    settings: decodeSafetyBoardSettings(value.settings),
  };
}

export function decodeSafetyBoardReadReceipt(value: unknown): SafetyBoardReadReceipt | null {
  if (!record(value) || !text(value.boardId) || value.read !== true) return null;
  if (!nullableText(value.readAt)) return null;
  return { boardId: value.boardId, read: true, readAt: value.readAt };
}

/**
 * Publisher authority, restated from the server-issued session scope.
 *
 * This is not a client-side role guess: `persona`, `capabilities` and `resource` all arrive on
 * the session the identity server issued, and the rule below is the deployed board wave's own
 * rule (`reader()` in firebase-safety-board-functions and `assertAuthority()` in
 * content-communications), reproduced so the UI does not offer a control the server will refuse.
 * The server re-checks every command regardless; a mismatch here can only hide a control, never
 * grant one.
 */
export type SafetyBoardAuthority = Readonly<{
  /** Sees unpublished posts and the manage controls. */
  publisher: boolean;
  /** `board.upsert` requires `finding:issue`. */
  canCompose: boolean;
  /** `board.publish` / `board.archive` / `board.display` require `document:submit`. */
  canRelease: boolean;
}>;

export const readerAuthority: SafetyBoardAuthority = {
  publisher: false,
  canCompose: false,
  canRelease: false,
};

export function safetyBoardAuthority(scope: {
  persona: Persona;
  capabilities: readonly DutyCapability[];
  resource: ResourceScope;
  featureAccess?: unknown;
  developerReadOnly?: boolean;
}): SafetyBoardAuthority {
  const feature = featureAccessProjectionStatus(scope.featureAccess);
  if (feature.kind === 'malformed') return readerAuthority;
  const has = (capability: DutyCapability) => scope.capabilities.includes(capability);
  const legacySitePublisher =
    scope.resource.kind === 'site' &&
    scope.resource.tenantId === 'kop' && /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(scope.resource.siteId) &&
    scope.persona === 'safety-manager';
  const enforcedSitePublisher = scope.resource.kind === 'site' && scope.resource.tenantId === 'kop' &&
    /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(scope.resource.siteId);
  const enforcedPublisher = feature.kind === 'enforced' && feature.features.has('safety-board') && !scope.developerReadOnly;
  const publisher = (feature.kind === 'enforced' ? enforcedSitePublisher && enforcedPublisher : legacySitePublisher && has('document:read'));
  return {
    publisher,
    canCompose: publisher && (feature.kind === 'enforced' || has('document:submit')),
    canRelease: publisher && (feature.kind === 'enforced' || has('document:submit')),
  };
}

/** A published record is immutable: `board.upsert` refuses it with `IMMUTABLE_RECORD`. */
export function postIsEditable(post: SafetyBoardPost) {
  return post.status === 'open' || post.status === 'published';
}

export function postIsPublishable(post: SafetyBoardPost) {
  return post.status === 'open';
}

export function postIsArchivable(post: SafetyBoardPost) {
  return post.status === 'published' || post.status === 'promoted';
}

export function postIsDeletable(post: SafetyBoardPost) {
  return post.status !== 'promoted';
}

const dateFormat = new Intl.DateTimeFormat('ko-KR', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Rows carry the publication date; an unpublished post has only its last change. */
export function postDate(post: SafetyBoardPost) {
  const value = new Date(post.publishedAt ?? post.updatedAt);
  return Number.isNaN(value.getTime()) ? '' : dateFormat.format(value);
}

/** Sorted the way the server sorts a page: pinned first, then newest change. */
export function orderedPosts(items: readonly SafetyBoardPost[]) {
  return [...items].sort((left, right) => {
    if (left.pinned !== right.pinned) return left.pinned ? -1 : 1;
    const priority = { urgent: 2, important: 1, normal: 0 } as const;
    if (priority[left.emphasis] !== priority[right.emphasis])
      return priority[right.emphasis] - priority[left.emphasis];
    return right.updatedAt.localeCompare(left.updatedAt);
  });
}

/**
 * Applies a server read receipt to a page. The row leaves the unread set only because the
 * server said so, and the page's unread tally -- which counts every visible post, not only the
 * rows on this page -- is stepped down by the one post the receipt covers.
 */
export function applyReadReceipt(
  page: SafetyBoardPage,
  receipt: SafetyBoardReadReceipt,
): SafetyBoardPage {
  const target = page.items.find((item) => item.boardId === receipt.boardId);
  if (!target || target.read) return page;
  return {
    ...page,
    items: page.items.map((item) =>
      item.boardId === receipt.boardId ? { ...item, read: true, readAt: receipt.readAt } : item,
    ),
    unreadVisible: Math.max(0, page.unreadVisible - 1),
  };
}

/**
 * A committed, server-verified upload manifest -- the receipt `getTrustedMediaAdapter().upload`
 * returns, never a client-side stand-in. `board.upsert` re-verifies both fields against the
 * manifest on every write, so a fabricated pair fails closed on the server rather than appearing
 * to attach.
 */
export type BoardAttachment = Readonly<{ manifestId: string; sha256: string }>;

export type SafetyBoardSettings = Readonly<{
  siteId: string;
  authority: Readonly<{ canManage: boolean }>;
  configuration: Readonly<{
    ready: boolean;
    issue: 'BOARD_POLICY_REQUIRED' | null;
    version: number;
  }>;
  categories: readonly Readonly<{
    id: string;
    label: string;
    order: number;
    color: BoardCategoryColor;
    icon: BoardCategoryIcon;
  }>[];
  maxImages: number;
  maxPinned: number;
}>;

export function decodeSafetyBoardSettings(value: unknown): SafetyBoardSettings | null {
  if (
    !record(value) ||
    typeof value.siteId !== 'string' || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(value.siteId) ||
    !record(value.authority) ||
    typeof value.authority.canManage !== 'boolean' ||
    !record(value.configuration) ||
    typeof value.configuration.ready !== 'boolean' ||
    !tally(value.configuration.version) ||
    !Array.isArray(value.categories)
  )
    return null;
  const categories = value.categories.flatMap((candidate) => {
    if (!record(candidate) || !text(candidate.id) || !text(candidate.label)) return [];
    if (typeof candidate.order !== 'number' || !Number.isFinite(candidate.order)) return [];
    if (
      (candidate.color !== undefined &&
        (!text(candidate.color) ||
          !(boardCategoryColors as readonly string[]).includes(candidate.color))) ||
      (candidate.icon !== undefined &&
        (!text(candidate.icon) ||
          !(boardCategoryIcons as readonly string[]).includes(candidate.icon)))
    )
      return [];
    return [
      {
        id: candidate.id,
        label: candidate.label,
        order: candidate.order,
        color: (candidate.color ?? 'navy') as BoardCategoryColor,
        icon: (candidate.icon ?? 'megaphone') as BoardCategoryIcon,
      },
    ];
  });
  if (
    (value.configuration.ready && categories.length === 0) ||
    categories.length !== value.categories.length ||
    !tally(value.maxImages) ||
    (value.maxPinned !== undefined && !tally(value.maxPinned)) ||
    (value.configuration.ready
      ? value.configuration.issue !== null
      : value.configuration.issue !== 'BOARD_POLICY_REQUIRED')
  )
    return null;
  return {
    siteId: value.siteId,
    authority: { canManage: value.authority.canManage },
    configuration: {
      ready: value.configuration.ready,
      issue: value.configuration.ready ? null : 'BOARD_POLICY_REQUIRED',
      version: value.configuration.version,
    },
    categories,
    maxImages: Math.min(6, value.maxImages),
    maxPinned: Math.min(3, value.maxPinned === undefined ? 3 : value.maxPinned),
  };
}

/** Command envelope for `executeSafetyBoardCommand`. */
export type SafetyBoardCommand = Readonly<{
  action: 'post' | 'create' | 'update' | 'publish' | 'archive' | 'delete' | 'display';
  commandId: string;
  idempotencyKey: string;
  boardId: string;
  expectedVersion: number;
  post?: Readonly<{
    category: string;
    title: string;
    body: string;
    audience: string;
    attachments: readonly BoardAttachment[];
  }>;
  display?: Readonly<{ pinned: boolean; titleColor: BoardTitleColor | null }>;
}>;

const identifier = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export function newBoardId() {
  return `board-${identifier()}`;
}

export function newCommandEnvelope() {
  const value = identifier();
  return { commandId: `cmd-${value}`, idempotencyKey: `idem-${value}` };
}

export function composeCommand(input: {
  boardId: string;
  expectedVersion: number;
  category: string;
  title: string;
  body: string;
  audience: string;
  attachments?: readonly BoardAttachment[];
}): SafetyBoardCommand {
  return {
    action: input.expectedVersion === 0 ? 'create' : 'update',
    ...newCommandEnvelope(),
    boardId: input.boardId,
    expectedVersion: input.expectedVersion,
    post: {
      category: input.category,
      title: input.title,
      body: input.body,
      audience: input.audience,
      attachments: input.attachments ?? [],
    },
  };
}

export function postCommand(input: {
  boardId: string;
  expectedVersion: number;
  category: string;
  title: string;
  body: string;
  attachments?: readonly BoardAttachment[];
}): SafetyBoardCommand {
  return {
    action: 'post',
    ...newCommandEnvelope(),
    boardId: input.boardId,
    expectedVersion: input.expectedVersion,
    post: {
      category: input.category,
      title: input.title,
      body: input.body,
      audience: 'site',
      attachments: input.attachments ?? [],
    },
  };
}

export function releaseCommand(
  action: 'publish' | 'archive' | 'delete',
  post: SafetyBoardPost,
): SafetyBoardCommand {
  return {
    action,
    ...newCommandEnvelope(),
    boardId: post.boardId,
    expectedVersion: post.version,
  };
}

/**
 * `display` is versioned against the sibling display document, not the post: `expectedVersion`
 * is `displayVersion`. Both keys are mandatory, so the current state of the one that is not
 * being changed is always sent back.
 */
export function displayCommand(
  post: SafetyBoardPost,
  change: Partial<Readonly<{ pinned: boolean; titleColor: BoardTitleColor }>>,
): SafetyBoardCommand {
  return displayValuesCommand({
    boardId: post.boardId,
    expectedVersion: post.displayVersion,
    pinned: change.pinned ?? post.pinned,
    titleColor: change.titleColor ?? post.titleColor,
  });
}

export function displayValuesCommand(input: {
  boardId: string;
  expectedVersion: number;
  pinned: boolean;
  titleColor: BoardTitleColor;
}): SafetyBoardCommand {
  return {
    action: 'display',
    ...newCommandEnvelope(),
    boardId: input.boardId,
    expectedVersion: input.expectedVersion,
    display: {
      pinned: input.pinned,
      titleColor: input.titleColor,
    },
  };
}
